/**
 * NotificationPreferencesSection.js — öğrencinin kendi iletişim izinlerini
 * (KVKK/6563 sayılı Kanun kapsamında) yönetebildiği bölüm. ProfilePage.js'in
 * "Bildirimler" sekmesinde ve `/hesap/bildirim-tercihleri` yönlendirmesinde
 * (ticari e-postadaki 6563 md.6 opt-out linkinin hedefi — bkz.
 * backend/routes/admin_bulk.py::_commercial_email_opt_out_footer) render edilir.
 *
 * API sözleşmesi (backend/routes/auth.py'deki gerçek endpoint'ler okunarak
 * DOĞRULANDI — commit a95d498, 2026-07-23):
 *   GET /users/me/communication-prefs → { service_email, service_whatsapp,
 *        commercial_email, commercial_whatsapp, updated_at }
 *   PUT /users/me/communication-prefs body — KISMİ güncelleme (sadece
 *        değişen alan(lar) gönderilir, gönderilmeyenler mevcut değerinde
 *        kalır) → güncel/çözümlenmiş 4 alan + updated_at.
 *
 * Varsayılanlar (backend hazır olmadığında/hata durumunda güvenli düşüş —
 * services/communication_prefs.py::DEFAULT_COMMUNICATION_PREFS ile BİREBİR
 * aynı): hizmet kanalları AÇIK, ticari kanallar KAPALI.
 *
 * NOT: Uygulama içi (in-app) bildirimler bu ekranda YÖNETİLMEZ — her zaman
 * gönderilir, kapatma seçeneği kasıtlı olarak yok (bkz. inAppNote metni;
 * backend da in_app kanalını izin kontrolsüz gönderiyor).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, Mail, MessageCircle, Megaphone, ShieldCheck, Info, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { normalizeCommunicationPrefs } from '../../utils/communicationPrefs';
import { Switch } from '../ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PrefToggle({ icon: Icon, label, checked, saving, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2" data-testid={testId}>
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
          <Icon size={16} />
        </div>
        <span className="font-semibold text-sm text-[var(--text-main)]">{label}</span>
      </div>
      <Switch
        checked={checked}
        disabled={saving}
        onCheckedChange={onChange}
        aria-label={label}
        data-testid={`${testId}-switch`}
      />
    </div>
  );
}

export default function NotificationPreferencesSection() {
  const { authHeaders } = useAuth();
  const { lang, t } = useLanguage();
  const toast = useToast();

  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchPrefs = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await axios.get(`${API}/users/me/communication-prefs`, { headers: authHeaders() });
      setPrefs(normalizeCommunicationPrefs(r.data));
      setUpdatedAt(r.data?.updated_at || null);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const handleToggle = async (key, nextValue) => {
    if (!prefs) return;
    const prevPrefs = prefs;
    const nextPrefs = { ...prefs, [key]: nextValue };
    setSavingKey(key);
    setPrefs(nextPrefs); // iyimser güncelleme
    try {
      // PUT kısmi güncelleme kabul eder (backend: model_dump(exclude_unset=True))
      // — sadece değişen alan gönderilir, diğer 3 alan backend'de mevcut
      // değerinde kalır. Tam obje göndermek de çalışırdı ama bu, "kısmi
      // güncelleme" sözleşmesine daha sadık ve yarış durumu riskini azaltır
      // (iki sekmeden aynı anda farklı alan değiştirilirse birbirini ezmez).
      const r = await axios.put(`${API}/users/me/communication-prefs`, { [key]: nextValue }, { headers: authHeaders() });
      setPrefs(normalizeCommunicationPrefs(r.data ?? nextPrefs));
      setUpdatedAt(r.data?.updated_at || new Date().toISOString());
      toast.success(t('profile.notifPrefs.saveSuccess'));
    } catch (err) {
      setPrefs(prevPrefs); // geri al
      toast.error(getApiErrorMessage(err, lang) || t('profile.notifPrefs.saveError'));
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <div className="card-clay p-8 flex items-center justify-center" data-testid="notif-prefs-loading">
        <div className="w-8 h-8 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !prefs) {
    return (
      <div className="card-clay p-6 text-center" data-testid="notif-prefs-error">
        <Bell size={32} className="mx-auto text-[var(--text-muted)] mb-2" />
        <p className="font-bold text-sm text-[var(--text-muted)] mb-3">{t('profile.notifPrefs.loadError')}</p>
        <button
          type="button"
          onClick={fetchPrefs}
          className="btn-tactile px-4 py-2 text-sm bg-white border-[var(--border-light)] inline-flex items-center gap-1.5"
          data-testid="notif-prefs-retry"
        >
          <RefreshCw size={14} /> {t('profile.notifPrefs.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="notif-prefs-section">
      <div className="card-clay p-5">
        <div className="flex items-center gap-2 mb-1">
          <Bell size={18} className="text-[var(--brand-primary)]" />
          <h3 className="font-bold text-sm">{t('profile.notifPrefs.title')}</h3>
        </div>
        <p className="text-xs text-[var(--text-muted)]">{t('profile.notifPrefs.subtitle')}</p>
      </div>

      {/* Hizmet bildirimleri — kapatılabilir */}
      <div className="card-clay p-5">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={16} className="text-emerald-600" />
          <h4 className="font-bold text-sm">{t('profile.notifPrefs.serviceTitle')}</h4>
        </div>
        <p className="text-xs text-[var(--text-muted)] mb-1">{t('profile.notifPrefs.serviceDesc')}</p>
        <div className="mt-2">
          <PrefToggle
            icon={Mail}
            label={t('profile.notifPrefs.serviceEmailLabel')}
            checked={prefs.service_email}
            saving={savingKey === 'service_email'}
            onChange={(v) => handleToggle('service_email', v)}
            testId="notif-pref-service-email"
          />
          <div className="border-t border-[var(--border-light)]" />
          <PrefToggle
            icon={MessageCircle}
            label={t('profile.notifPrefs.serviceWhatsappLabel')}
            checked={prefs.service_whatsapp}
            saving={savingKey === 'service_whatsapp'}
            onChange={(v) => handleToggle('service_whatsapp', v)}
            testId="notif-pref-service-whatsapp"
          />
        </div>
      </div>

      {/* Ticari iletiler — varsayılan kapalı, açık rıza gerektirir */}
      <div className="card-clay p-5">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone size={16} className="text-amber-600" />
          <h4 className="font-bold text-sm">{t('profile.notifPrefs.commercialTitle')}</h4>
        </div>
        <p className="text-xs text-[var(--text-muted)] mb-1">{t('profile.notifPrefs.commercialDesc')}</p>
        <div className="mt-2">
          <PrefToggle
            icon={Mail}
            label={t('profile.notifPrefs.commercialEmailLabel')}
            checked={prefs.commercial_email}
            saving={savingKey === 'commercial_email'}
            onChange={(v) => handleToggle('commercial_email', v)}
            testId="notif-pref-commercial-email"
          />
          <div className="border-t border-[var(--border-light)]" />
          <PrefToggle
            icon={MessageCircle}
            label={t('profile.notifPrefs.commercialWhatsappLabel')}
            checked={prefs.commercial_whatsapp}
            saving={savingKey === 'commercial_whatsapp'}
            onChange={(v) => handleToggle('commercial_whatsapp', v)}
            testId="notif-pref-commercial-whatsapp"
          />
        </div>
        <p className="text-[11px] text-[var(--text-muted)] mt-3 flex items-start gap-1.5">
          <Info size={12} className="shrink-0 mt-0.5" />
          {t('profile.notifPrefs.consentNote')}
        </p>
      </div>

      {/* Uygulama içi bildirimler — her zaman açık, bilgilendirme amaçlı */}
      <div className="rounded-xl bg-sky-50 border-2 border-sky-100 p-3 flex items-start gap-2 text-xs text-sky-800" data-testid="notif-pref-in-app-note">
        <Info size={14} className="shrink-0 mt-0.5" />
        <span>{t('profile.notifPrefs.inAppNote')}</span>
      </div>

      {updatedAt && (
        <p className="text-[10px] text-[var(--text-muted)] text-right" data-testid="notif-pref-updated-at">
          {t('profile.notifPrefs.updatedAt').replace('{{date}}', new Date(updatedAt).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US'))}
        </p>
      )}
    </div>
  );
}
